/**
 * `/liturgies/{id}/people` — who serves.
 *
 * From the mockup's `isPeople` block: three groupings — **Roles in use**, **Available, not in use**
 * ("not every celebration needs every position"), and **Extraordinary Form Roles**, shown greyed and
 * permanently unavailable — with an aside explaining where a Role comes from, who may fill one, and
 * the rule the whole screen rests on:
 *
 * > A Person is not a permission. Assigning someone to a Role records who serves. It grants no
 * > authority in the workspace — that comes only from a Capability.
 *
 * That copy is not decoration. It is the one place a user is told that liturgical role and
 * application authority are separate, and Cantor ≠ Administrator
 * (docs/engineering/11-screens.md).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import { WorkspaceTabs } from '../../components/composites/WorkspaceTabs';
import {
  addLocalRole,
  addPerson,
  assignPerson,
  confirmAssignment,
  fetchRoster,
  removeRole,
  restoreRole,
  unassign,
  type Person,
  type RoleOrigin,
  type RosterRole,
} from './api';
import './people.css';

const ORIGIN_KEY: Record<RoleOrigin, string> = {
  constraint: 'people.origin_constraint',
  template: 'people.origin_template',
  local: 'people.origin_local',
};

/** Role-facing labels, which differ from the Item-facing ones the policy screen uses. */
const ACCESS_KEY: Record<string, string> = {
  selectable: 'people.selectable_title',
  read_only: 'people.admin_selected_title',
  hidden: 'people.hidden_title',
};

export function PeopleScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/people' });
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const roster = useQuery({ queryKey: ['roster', id], queryFn: () => fetchRoster(id) });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['roster', id] });
    // The overview's blockers panel counts unfilled Constraint Roles.
    await queryClient.invalidateQueries({ queryKey: ['liturgy', id] });
  };

  if (roster.isPending) return <p className="ppl-state">Loading the roster…</p>;

  if (roster.isError) {
    const error = roster.error as ApiError;
    return (
      <div className="ppl">
        <Notice referenceId={error.correlationId}>{error.message}</Notice>
      </div>
    );
  }

  const view = roster.data;

  return (
    <div className="ppl">
      <header className="ppl__head">
        <div>
          <h1 className="ppl__title">People &amp; roles</h1>
          <p className="ppl__lede">
            The Template declares which Roles exist. This Liturgy&rsquo;s Selection Policy decides
            which of them are offered and who may fill each one.
          </p>
        </div>
        <Link className="ppl__link" to="/liturgies/$id/selection-policy" params={{ id }}>
          {t('workspace.selection_policy')} →
        </Link>
      </header>

      <WorkspaceTabs liturgyId={id} />

      <div className="ppl__grid">
        <div className="ppl__main">
          <section className="ppl__panel">
            <div className="ppl__panel-head">
              <div>
                <h2 className="ppl__panel-title">{t('people.roles_in_use')}</h2>
                <p className="ppl__panel-sub">
                  Declared by the Template, scoped by this Liturgy&rsquo;s Selection Policy
                </p>
              </div>
            </div>

            {view.roles_in_use.length === 0 ? (
              <p className="ppl__panel-note">No roles are in use on this celebration.</p>
            ) : (
              view.roles_in_use.map((role) => (
                <RoleRow
                  key={role.id}
                  liturgyId={id}
                  role={role}
                  people={view.people}
                  mayManage={view.may_manage}
                  expanded={expanded === role.id}
                  onToggle={() => setExpanded(expanded === role.id ? null : role.id)}
                  onChanged={refresh}
                />
              ))
            )}

            <p className="ppl__panel-note">
              Changes here bind this Liturgy only and are recorded in History. A Constraint Role
              cannot be withdrawn.
            </p>
          </section>

          {view.roles_extraordinary_form.length > 0 ? null : <ExtraordinaryFormPanel />}

          <section className="ppl__panel">
            <div className="ppl__panel-head">
              <div>
                <h2 className="ppl__panel-title">{t('people.roles_available')}</h2>
                <p className="ppl__panel-sub">{t('people.roles_aside_heading')}</p>
              </div>
            </div>

            {view.roles_available.length === 0 ? (
              <p className="ppl__panel-note">Every declared Role is in use.</p>
            ) : (
              view.roles_available.map((role) => (
                <div key={role.id} className="ppl__available">
                  <div className="ppl__available-name">
                    <div className="ppl__role-label">{role.label}</div>
                    <div className="ppl__role-meta">{t(ORIGIN_KEY[role.origin])}</div>
                  </div>
                  <div className="ppl__available-why">
                    {role.origin === 'local'
                      ? 'Removed from this celebration&rsquo;s roster.'
                      : 'Withdrawn by the Selection Policy for this celebration.'}
                  </div>
                  <div className="ppl__available-act">
                    {role.origin === 'local' ? (
                      <RestoreButton liturgyId={id} roleId={role.id} onChanged={refresh} />
                    ) : (
                      // A Template Role comes back by editing the Policy, not by a roster button.
                      // Offering "Add" here would imply the roster can override the Policy.
                      <Link
                        className="ppl__btn ppl__btn--small"
                        to="/liturgies/$id/selection-policy"
                        params={{ id }}
                      >
                        In Policy
                      </Link>
                    )}
                  </div>
                </div>
              ))
            )}

            <p className="ppl__panel-note">
              Adding a Role here affects this Liturgy only. To change what every Liturgy starts
              with, edit the Template roster.
            </p>

            {view.may_manage ? <AddRoleForm liturgyId={id} onChanged={refresh} /> : null}
          </section>

          <PeoplePanel liturgyId={id} people={view.people} onChanged={refresh} />
        </div>

        <aside className="ppl__aside">
          <div className="ppl__card">
            <p className="ppl__eyebrow">{t('people.where_role_comes_from')}</p>
            <dl className="ppl__legend">
              <div>
                <dt className="ppl__origin ppl__origin--constraint">
                  {t('people.origin_constraint')}
                </dt>
                <dd>
                  The Template requires it. Present in every Liturgy, not removable, and blocks
                  Finalization while unassigned.
                </dd>
              </div>
              <div>
                <dt className="ppl__origin ppl__origin--template">{t('people.origin_template')}</dt>
                <dd>
                  Offered by the Template roster as a sensible default. Removable per Liturgy
                  without touching the Template.
                </dd>
              </div>
              <div>
                <dt className="ppl__origin ppl__origin--local">{t('people.origin_local')}</dt>
                <dd>Added for this celebration. Recorded in History with who added it.</dd>
              </div>
            </dl>
          </div>

          <div className="ppl__card">
            <p className="ppl__eyebrow">{t('people.who_may_fill')}</p>
            <p className="ppl__card-text">
              Set per Role by the Liturgy Selection Policy, not by the Template. The Template says a
              Role exists; the Policy says who may propose a Person for it.
            </p>
            <dl className="ppl__legend ppl__legend--tight">
              <div>
                <dt>{t('people.selectable_title')}</dt>
                <dd>{t('people.selectable_desc')}</dd>
              </div>
              <div>
                <dt>{t('people.admin_selected_title')}</dt>
                <dd>{t('people.admin_selected_desc')}</dd>
              </div>
              <div>
                <dt>{t('people.hidden_title')}</dt>
                <dd>{t('people.hidden_desc')}</dd>
              </div>
            </dl>
            <Link className="ppl__link" to="/liturgies/$id/selection-policy" params={{ id }}>
              Configure Selection Policy →
            </Link>
          </div>

          <div className="ppl__card ppl__card--warm">
            <p className="ppl__eyebrow">A Person is not a permission</p>
            <p className="ppl__card-text">
              Assigning someone to a Role records who serves. It grants no authority in the
              workspace — that comes only from a Capability, issued and revoked under Access.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ── one role ────────────────────────────────────────────────────────────────

function RoleRow({
  liturgyId,
  role,
  people,
  mayManage,
  expanded,
  onToggle,
  onChanged,
}: {
  liturgyId: string;
  role: RosterRole;
  people: Person[];
  mayManage: boolean;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [personId, setPersonId] = useState('');

  const assign = useMutation({
    mutationFn: () => assignPerson(liturgyId, role.id, { person_id: personId }),
    onSuccess: async () => {
      setPersonId('');
      await onChanged();
    },
  });
  const confirm = useMutation({
    mutationFn: (assignmentId: string) => confirmAssignment(liturgyId, assignmentId),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: (assignmentId: string) => unassign(liturgyId, assignmentId),
    onSuccess: onChanged,
  });
  const removeThisRole = useMutation({
    mutationFn: () => removeRole(liturgyId, role.id),
    onSuccess: onChanged,
  });

  const error = [assign.error, confirm.error, remove.error, removeThisRole.error].find(
    (e): e is ApiError => e instanceof Error && e.name === 'ApiError',
  );

  const unassigned = people.filter(
    (person) => !role.assignments.some((a) => a.person_id === person.id),
  );

  const blocking =
    role.origin === 'constraint' && !role.assignments.some((a) => a.state === 'confirmed');

  return (
    <div className={`ppl__role${blocking ? ' ppl__role--blocking' : ''}`}>
      <button type="button" className="ppl__role-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="ppl__role-name">
          <span className="ppl__role-label">{role.label}</span>
        </span>
        <span className={`ppl__origin ppl__origin--${role.origin}`}>
          {t(ORIGIN_KEY[role.origin])}
        </span>
        <span className="ppl__access">{t(ACCESS_KEY[role.access_mode] ?? role.access_mode)}</span>
        <span className="ppl__slots">
          {role.assignments.length === 0
            ? 'Unassigned'
            : role.assignments.map((a) => a.person_name).join(', ')}
        </span>
        <span className="ppl__chev">›</span>
      </button>

      {expanded ? (
        <div className="ppl__role-body">
          {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

          {role.policy_note ? <p className="ppl__note">{role.policy_note}</p> : null}

          {blocking ? (
            <p className="ppl__note ppl__note--blocking">
              A Constraint Role blocks Finalization until someone is confirmed in it. A proposal
              alone is not enough.
            </p>
          ) : null}

          {role.assignments.length === 0 ? (
            <p className="ppl__note">{t('people.nobody_assigned_role')}</p>
          ) : (
            <ul className="ppl__assignments">
              {role.assignments.map((a) => (
                <li key={a.id}>
                  <span className="ppl__person">{a.person_name}</span>
                  <span
                    className={`ppl__pill ppl__pill--${a.state === 'confirmed' ? 'confirmed' : 'proposed'}`}
                  >
                    {a.state === 'confirmed' ? 'Confirmed' : 'Proposed'}
                  </span>
                  {a.proposed_by ? (
                    <span className="ppl__by">proposed by {a.proposed_by}</span>
                  ) : null}
                  {mayManage ? (
                    <span className="ppl__assignment-acts">
                      {a.state === 'proposed' ? (
                        <button
                          type="button"
                          className="ppl__btn ppl__btn--small"
                          disabled={confirm.isPending}
                          onClick={() => confirm.mutate(a.id)}
                        >
                          Confirm
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="ppl__btn ppl__btn--small ppl__btn--quiet"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(a.id)}
                      >
                        Remove
                      </button>
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {unassigned.length > 0 ? (
            <div className="ppl__assign">
              <label className="ppl__field">
                <span className="visually-hidden">Person to assign to {role.label}</span>
                <select value={personId} onChange={(e) => setPersonId(e.target.value)}>
                  <option value="">{t('people.choose_person')}</option>
                  {unassigned.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="ppl__btn"
                disabled={!personId || assign.isPending}
                onClick={() => assign.mutate()}
              >
                {/* The wording matches what will actually happen, which the server decides. */}
                {mayManage ? 'Assign' : 'Propose'}
              </button>
            </div>
          ) : (
            <p className="ppl__note">
              Everyone on this celebration&rsquo;s list is already assigned to this role. Add
              someone below first.
            </p>
          )}

          {role.removable && mayManage ? (
            <button
              type="button"
              className="ppl__btn ppl__btn--danger"
              disabled={removeThisRole.isPending}
              onClick={() => removeThisRole.mutate()}
            >
              Remove Role from this Liturgy
            </button>
          ) : role.origin === 'constraint' ? (
            <p className="ppl__note">
              A Constraint Role cannot be withdrawn by Policy. The Policy may still narrow who fills
              it.
            </p>
          ) : (
            <p className="ppl__note">
              A Template Role is withdrawn through the Selection Policy, not removed here.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── panels ──────────────────────────────────────────────────────────────────

function ExtraordinaryFormPanel(): React.JSX.Element {
  const { t } = useTranslation('common');
  return (
    <section className="ppl__panel ppl__panel--muted">
      <div className="ppl__panel-head">
        <div>
          <h2 className="ppl__panel-title">{t('people.ef_roles_title')}</h2>
          <p className="ppl__panel-sub">{t('people.ef_roles_desc')}</p>
        </div>
      </div>
      <p className="ppl__panel-note">
        These Roles are <strong>scoped exclusively to Extraordinary Form Templates</strong>, none of
        which this deployment publishes. They are listed to show that the Role model already
        accommodates them: a future Extraordinary Form Template declares them in its roster and they
        become available to Liturgies using that Template only. They cannot be added to an Ordinary
        Form Liturgy, and no Selection Policy can offer them here.
      </p>
      <p className="ppl__panel-note">
        Enabling the Extraordinary Form is a Template decision, not a per-Liturgy one. Publishing
        such a Template is the only route by which these Roles become selectable.
      </p>
    </section>
  );
}

function PeoplePanel({
  liturgyId,
  people,
  onChanged,
}: {
  liturgyId: string;
  people: Person[];
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');

  const add = useMutation({
    mutationFn: () => addPerson(liturgyId, { display_name: name, contact: contact || null }),
    onSuccess: async () => {
      setName('');
      setContact('');
      await onChanged();
    },
  });

  return (
    <section className="ppl__panel">
      <div className="ppl__panel-head">
        <div>
          <h2 className="ppl__panel-title">{t('people.people_celebration_title')}</h2>
          <p className="ppl__panel-sub">{t('people.people_celebration_desc')}</p>
        </div>
      </div>

      {people.length === 0 ? (
        <p className="ppl__panel-note">{t('people.nobody_added')}</p>
      ) : (
        <ul className="ppl__people">
          {people.map((person) => (
            <li key={person.id}>
              <span className="ppl__person">{person.display_name}</span>
              {person.contact ? <span className="ppl__by">{person.contact}</span> : null}
            </li>
          ))}
        </ul>
      )}

      {add.error instanceof Error ? (
        <Notice referenceId={(add.error as ApiError).correlationId}>{add.error.message}</Notice>
      ) : null}

      <div className="ppl__assign">
        <label className="ppl__field">
          <span className="ppl__field-label">{t('people.name')}</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="ppl__field">
          <span className="ppl__field-label">{t('people.contact_optional')}</span>
          <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} />
        </label>
        <button
          type="button"
          className="ppl__btn"
          disabled={name.trim().length === 0 || add.isPending}
          onClick={() => add.mutate()}
        >
          Add person
        </button>
      </div>

      <p className="ppl__panel-note">
        People belong to this celebration. There is no parish-wide directory yet — that needs
        Organizations, and holding a list of named parishioners is a materially different thing to
        store than a roster for one liturgy.
      </p>
    </section>
  );
}

function AddRoleForm({
  liturgyId,
  onChanged,
}: {
  liturgyId: string;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [label, setLabel] = useState('');
  const [multiple, setMultiple] = useState(false);

  const add = useMutation({
    mutationFn: () => addLocalRole(liturgyId, { label, allows_multiple: multiple }),
    onSuccess: async () => {
      setLabel('');
      setMultiple(false);
      await onChanged();
    },
  });

  return (
    <div className="ppl__assign">
      <label className="ppl__field">
        <span className="ppl__field-label">{t('people.add_role_celebration')}</span>
        <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} />
      </label>
      <label className="ppl__check">
        <input type="checkbox" checked={multiple} onChange={(e) => setMultiple(e.target.checked)} />
        <span>{t('people.several_may_serve')}</span>
      </label>
      <button
        type="button"
        className="ppl__btn"
        disabled={label.trim().length === 0 || add.isPending}
        onClick={() => add.mutate()}
      >
        Add Role
      </button>
    </div>
  );
}

function RestoreButton({
  liturgyId,
  roleId,
  onChanged,
}: {
  liturgyId: string;
  roleId: string;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const restore = useMutation({
    mutationFn: () => restoreRole(liturgyId, roleId),
    onSuccess: onChanged,
  });

  return (
    <button
      type="button"
      className="ppl__btn ppl__btn--small"
      disabled={restore.isPending}
      onClick={() => restore.mutate()}
    >
      Add
    </button>
  );
}
